import * as E from "fp-ts/Either"
import { BehaviorSubject } from "rxjs"
import { platform } from "~/platform"
import { GQLError } from "../backend/GQLClient"
import { GetMyTeamsQuery } from "../backend/graphql"

const BACKEND_PAGE_SIZE = 10
const POLL_DURATION = 10000

export default class TeamListAdapter {
  error$: BehaviorSubject<GQLError<string> | null>
  loading$: BehaviorSubject<boolean>
  teamList$: BehaviorSubject<GetMyTeamsQuery["myTeams"]>

  private timeoutHandle: ReturnType<typeof setTimeout> | null
  private generation = 0
  private fetchPromise: Promise<void> | null = null

  public isInitialized: boolean

  constructor(
    deferInit = false,
    private doPolling = true
  ) {
    this.error$ = new BehaviorSubject<GQLError<string> | null>(null)
    this.loading$ = new BehaviorSubject<boolean>(false)
    this.teamList$ = new BehaviorSubject<GetMyTeamsQuery["myTeams"]>([])
    this.timeoutHandle = null

    this.isInitialized = false

    if (!deferInit) this.initialize()
  }

  initialize() {
    if (this.isInitialized) throw new Error(`Adapter already initialized`)

    this.isInitialized = true
    const generation = ++this.generation

    const poll = async () => {
      await this.fetchList()

      if (
        this.isInitialized &&
        this.generation === generation &&
        this.doPolling
      ) {
        this.timeoutHandle = setTimeout(() => void poll(), POLL_DURATION)
      }
    }

    void poll()
  }

  public dispose() {
    this.generation++
    this.isInitialized = false
    this.fetchPromise = null
    if (this.timeoutHandle) clearTimeout(this.timeoutHandle)
    this.timeoutHandle = null
    this.teamList$.next([])
    this.error$.next(null)
    this.loading$.next(false)
  }

  async fetchList() {
    if (!this.isInitialized) return
    if (this.fetchPromise) return this.fetchPromise

    const currentUser = platform.auth.getCurrentUser()
    if (!currentUser) return

    const generation = this.generation
    const isCurrent = () =>
      this.isInitialized &&
      this.generation === generation &&
      platform.auth.getCurrentUser()?.uid === currentUser.uid

    this.loading$.next(true)

    const fetch = async () => {
      try {
        if (platform.auth.getProbableUser() !== null) {
          await platform.auth.waitProbableLoginToConfirm()
        }
        if (!isCurrent()) return

        const results: GetMyTeamsQuery["myTeams"] = []

        while (true) {
          const cursor =
            results.length > 0 ? results[results.length - 1].id : undefined
          const result = await platform.backend.getUserTeams(cursor)
          if (!isCurrent()) return

          if (E.isLeft(result)) {
            this.error$.next(result.left)
            return
          }

          results.push(...result.right.myTeams)
          if (result.right.myTeams.length !== BACKEND_PAGE_SIZE) break
        }

        this.teamList$.next(results)
        this.error$.next(null)
      } catch (error) {
        if (isCurrent()) {
          this.error$.next({
            type: "network_error",
            error: error instanceof Error ? error : new Error(String(error)),
          })
        }
      } finally {
        if (isCurrent()) this.loading$.next(false)
      }
    }

    const fetchPromise = fetch().finally(() => {
      if (this.fetchPromise === fetchPromise) this.fetchPromise = null
    })
    this.fetchPromise = fetchPromise
    return fetchPromise
  }
}
